import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowUpRight, Clock, BarChart3, MapPin, Check, Users, Target, BookOpen, Sparkles, Calendar } from "lucide-react";
import SEO from "@/components/SEO";
import { getFormationBySlug, formations } from "@/data/formations";
import CTASection from "@/components/sections/CTASection";

export default function FormationDetail() {
  const { slug } = useParams();
  const f = getFormationBySlug(slug);
  if (!f) return <Navigate to="/formations" replace />;

  const others = formations.filter((x) => x.slug !== slug);
  const courseSchema = {
    "@context": "https://schema.org",
    "@type": "Course",
    "name": f.title,
    "description": f.summary,
    "provider": {
      "@type": "Organization",
      "name": "YAFORMIS",
      "sameAs": "https://www.gme-assistance.fr"
    }
  };

  return (
    <>
      <SEO title={f.seoTitle.replace(" | YAFORMIS", "").replace(" | YAFORMIS", "")} description={f.metaDescription} path={`/formations/${f.slug}`} image={f.image} schema={courseSchema} />

      {/* Hero */}
      <section className="bg-brand-page py-12 md:py-16 border-b border-brand-line" data-testid="formation-hero">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <Link to="/formations" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-brand-ink transition-colors" data-testid="formation-back-link">
            ← Toutes les formations
          </Link>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mt-6 items-start">
            <div className="lg:col-span-7">
              <div className="num-marker">{f.number} / 04 — {f.level}</div>
              <h1 className="font-heading font-black tracking-tighter text-3xl sm:text-4xl lg:text-5xl mt-3 leading-[1.08] text-brand-ink hyphens-none">
                {f.title}
              </h1>
              <p className="mt-4 text-lg md:text-xl text-slate-600 leading-relaxed">
                {f.tagline}
              </p>
              <p className="mt-3 text-base text-slate-600 leading-relaxed">{f.summary}</p>

              <div className="mt-7 grid grid-cols-2 md:grid-cols-4 gap-px bg-brand-line border border-brand-line">
                <Stat icon={Clock} label="Durée" value={f.duration} />
                <Stat icon={BarChart3} label="Niveau" value={f.level} />
                <Stat icon={MapPin} label="Format" value={f.format} />
                <Stat icon={Sparkles} label="Tarif" value={f.price} />
              </div>

              <div className="mt-7 flex flex-wrap gap-3">
                <Link to="/contact" className="btn-cta" data-testid="formation-cta-primary">
                  Demander un devis
                  <ArrowUpRight className="w-5 h-5" />
                </Link>
                <a href="#programme" className="btn-ghost" data-testid="formation-cta-secondary">
                  Voir le programme
                </a>
              </div>

              {f.sessions && f.sessions.length > 0 && (
                <div className="mt-7 border border-brand-line bg-white p-5" data-testid="formation-sessions">
                  <div className="flex items-center gap-2 text-[10px] font-bold tracking-[0.2em] uppercase text-brand-blue">
                    <Calendar className="w-3.5 h-3.5" />
                    Prochaines sessions · Places limitées
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {f.sessions.map((s) => (
                      <span
                        key={s}
                        className="inline-flex items-center px-3 py-1.5 bg-brand-surface border border-brand-line text-sm font-bold text-brand-ink"
                        data-testid={`formation-session-${s.replace(/\s/g, '-')}`}
                      >
                        {s}
                      </span>
                    ))}
                  </div>
                  <p className="mt-3 text-xs text-slate-500 leading-relaxed">
                    Sessions inter-entreprises à Sarcelles ou en distanciel. Session intra-entreprise possible à la date de votre choix — <Link to="/contact" className="text-brand-blue font-semibold hover:underline">demandez un devis</Link>.
                  </p>
                </div>
              )}
            </div>
            <div className="lg:col-span-5">
              <div className="relative aspect-[4/3] bg-[#0a1830] overflow-hidden">
                <img src={f.image} alt={f.title} className="absolute inset-0 w-full h-full object-cover" data-testid="formation-image" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Objectifs */}
      <section className="bg-brand-surface py-14 md:py-20" data-testid="formation-objectives">
        <div className="max-w-7xl mx-auto px-5 md:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="overline">Objectifs</div>
            <h2 className="font-heading font-black tracking-tighter text-3xl sm:text-4xl lg:text-5xl mt-3 leading-[1.05] text-brand-ink">
              Ce que vous saurez faire à la fin.
            </h2>
          </div>
          <ul className="lg:col-span-7 space-y-3">
            {f.objectives.map((o, i) => (
              <li key={i} className="flex items-start gap-4 bg-white border border-brand-line p-5 hover:border-brand-ink transition-colors">
                <span className="num-marker text-brand-blue shrink-0 mt-1">{String(i + 1).padStart(2, "0")}</span>
                <span className="text-brand-ink leading-relaxed">{o}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Programme */}
      <section id="programme" className="bg-brand-page py-14 md:py-20 scroll-mt-24" data-testid="formation-program">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="overline">Programme détaillé</div>
          <h2 className="font-heading font-black tracking-tighter text-3xl sm:text-4xl lg:text-5xl mt-3 leading-[1.05] text-brand-ink">
            {f.duration} structurés en modules.
          </h2>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-px bg-brand-line border border-brand-line">
            {f.program.map((m, idx) => (
              <div key={idx} className="bg-white p-7 md:p-8" data-testid={`formation-module-${idx}`}>
                <BookOpen className="w-6 h-6 text-brand-blue" strokeWidth={1.6} />
                <h3 className="font-heading font-bold text-2xl mt-4 text-brand-ink">{m.title}</h3>
                <ul className="mt-4 space-y-2.5">
                  {m.items.map((it, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm text-slate-700 leading-relaxed">
                      <Check className="w-4 h-4 text-brand-cta mt-0.5 shrink-0" />
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Public + prérequis + méthodes */}
      <section className="bg-brand-ink text-white py-14 md:py-20 relative overflow-hidden" data-testid="formation-details">
        <div className="absolute inset-0 bg-grid-dark opacity-50 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-5 md:px-8 grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
          <Block icon={Users} title="Public cible">
            <ul className="space-y-2 text-slate-300">
              {f.audience.map((a, i) => (
                <li key={i} className="flex items-start gap-2"><span className="text-brand-cta mt-1">·</span>{a}</li>
              ))}
            </ul>
          </Block>
          <Block icon={Target} title="Prérequis">
            <p className="text-slate-300 leading-relaxed">{f.prerequisites}</p>
          </Block>
          <Block icon={Sparkles} title="Méthodes pédagogiques">
            <ul className="space-y-2 text-slate-300">
              {f.methods.map((m, i) => (
                <li key={i} className="flex items-start gap-2"><span className="text-brand-cta mt-1">·</span>{m}</li>
              ))}
            </ul>
          </Block>
        </div>
      </section>

      {/* Résultats */}
      <section className="bg-brand-page py-14 md:py-20" data-testid="formation-outcomes">
        <div className="max-w-7xl mx-auto px-5 md:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="overline">Résultats concrets</div>
            <h2 className="font-heading font-black tracking-tighter text-3xl sm:text-4xl lg:text-5xl mt-3 leading-[1.05] text-brand-ink">
              Ce que vous repartez avec.
            </h2>
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-px bg-brand-line border border-brand-line">
            {f.outcomes.map((o, i) => (
              <div key={i} className="bg-brand-surface p-6">
                <div className="num-marker">RÉSULTAT {String(i + 1).padStart(2, "0")}</div>
                <p className="mt-3 font-heading font-semibold text-lg text-brand-ink leading-snug">{o}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Autres formations */}
      <section className="bg-brand-surface py-14 md:py-20" data-testid="formation-others">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="overline">Autres parcours</div>
          <h2 className="font-heading font-black tracking-tighter text-2xl sm:text-3xl lg:text-4xl mt-3 leading-[1.1] text-brand-ink">
            Pour aller plus loin.
          </h2>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
            {others.map((o) => (
              <Link
                key={o.slug}
                to={`/formations/${o.slug}`}
                data-testid={`formation-related-${o.slug}`}
                className="group bg-white border border-brand-line hover:border-brand-ink transition-colors flex flex-col"
              >
                <div className="aspect-[16/10] overflow-hidden bg-[#0a1830]">
                  <img src={o.image} alt={o.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-6 flex-1 flex flex-col">
                  <div className="num-marker">{o.number}</div>
                  <h3 className="font-heading font-bold text-xl mt-3 leading-snug text-brand-ink">{o.title}</h3>
                  <div className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-brand-blue">
                    Voir la formation <ArrowUpRight className="w-4 h-4" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="bg-white p-4 md:p-5">
      <Icon className="w-4 h-4 text-brand-blue" />
      <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mt-2">{label}</div>
      <div className="font-heading font-bold text-base md:text-lg mt-1 text-brand-ink leading-tight">{value}</div>
    </div>
  );
}

function Block({ icon: Icon, title, children }) {
  return (
    <div>
      <Icon className="w-7 h-7 text-brand-cta" strokeWidth={1.6} />
      <h3 className="font-heading font-bold text-2xl mt-4">{title}</h3>
      <div className="mt-4 leading-relaxed">{children}</div>
    </div>
  );
}
